/**
 * Month view: a calendar of day types, the month's totals, and the timesheet.
 *
 * Totals cover full and half days, the billable day equivalent, how the month
 * split across workstreams, expenses by category and total distance.
 */

import { button, clear, el, toast } from '../lib/dom.js';
import {
  WEEKDAYS_SHORT,
  addMonths,
  formatMonth,
  monthDays,
  monthOf,
  isWeekend,
  today,
  weekdayIndex,
} from '../lib/dates.js';
import {
  billableValue,
  formatDays,
  formatKm,
  formatMoney,
  locationLabel,
  workstreamsForDay,
} from '../lib/model.js';
import * as store from '../lib/store.js';
import { printTimesheet } from '../lib/print.js';
import { exportMonthCsv } from '../lib/export.js';

const isMonthKey = (value) => typeof value === 'string' && /^\d{4}-\d{2}$/.test(value);

export async function renderMonth(container, params, app) {
  const monthKey = isMonthKey(params.date) ? params.date : monthOf(today());
  const settings = await store.getSettings();
  const days = await store.getDaysInMonth(monthKey);
  const byDate = new Map(days.map((d) => [d.date, d]));

  clear(container);

  container.append(
    el('div', { class: 'daynav' }, [
      button('‹', {
        class: 'btn btn--icon',
        'aria-label': 'Previous month',
        title: 'Previous month',
        onclick: () => app.go(`#/month/${addMonths(monthKey, -1)}`),
      }),
      button('›', {
        class: 'btn btn--icon',
        'aria-label': 'Next month',
        title: 'Next month',
        onclick: () => app.go(`#/month/${addMonths(monthKey, 1)}`),
      }),
      el('div', { class: 'daynav__date' }, [el('h2', { class: 'daynav__title' }, formatMonth(monthKey))]),
      el('input', {
        type: 'month',
        class: 'input',
        value: monthKey,
        'aria-label': 'Choose a month',
        onchange: (event) => {
          if (isMonthKey(event.target.value)) app.go(`#/month/${event.target.value}`);
        },
      }),
      monthKey === monthOf(today())
        ? null
        : button('This month', { class: 'btn btn--small', onclick: () => app.go('#/month') }),
      button('Timesheet', {
        class: 'btn btn--primary',
        onclick: () => printTimesheet({ monthKey, days, settings }),
      }),
      button('Export CSV', {
        class: 'btn',
        title: 'Downloads two files: days and expenses',
        onclick: () => {
          const result = exportMonthCsv(monthKey, days);
          toast(`Exported ${result.days} days and ${result.expenses} expenses.`);
        },
      }),
    ]),
  );

  container.append(renderCalendar(monthKey, byDate, app));
  container.append(renderTotals(days));
  container.append(renderWorkstreams(days, settings));
  container.append(renderExpenses(days, settings));
}

// --- Calendar ---------------------------------------------------------------

/** Short marker for the calendar cell. */
function dayTypeMark(dayType) {
  return { Full: 'Full', Half: 'Half', 'Non-billable': 'Non-bill.', 'Not working': '—' }[dayType] || '';
}

function renderCalendar(monthKey, byDate, app) {
  const dates = monthDays(monthKey);
  const grid = el('div', { class: 'calendar' });

  for (const name of WEEKDAYS_SHORT) {
    grid.append(el('div', { class: 'calendar__head' }, name));
  }

  // Blank cells before the first of the month, so the 1st lands on the right day.
  for (let i = 0; i < weekdayIndex(dates[0]); i += 1) {
    grid.append(el('div', { class: 'calendar__cell calendar__cell--blank' }));
  }

  for (const date of dates) {
    const day = byDate.get(date);
    const classes = ['calendar__cell'];
    if (isWeekend(date)) classes.push('calendar__cell--weekend');
    if (date === today()) classes.push('calendar__cell--today');
    if (day && day.dayType === 'Full') classes.push('calendar__cell--full');
    if (day && day.dayType === 'Half') classes.push('calendar__cell--half');

    const cell = el(
      'a',
      {
        class: classes.join(' '),
        href: `#/today/${date}`,
        title: day ? `${day.dayType} · ${locationLabel(day)}` : 'Nothing recorded',
      },
      [
        el('span', { class: 'calendar__num' }, String(Number(date.slice(8)))),
        day ? el('span', { class: 'calendar__type' }, dayTypeMark(day.dayType)) : null,
        day && day.done.length ? el('span', { class: 'calendar__dot', 'aria-hidden': 'true' }) : null,
      ],
    );
    grid.append(cell);
  }

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Calendar'),
      el('p', { class: 'card__hint' }, 'A dot marks a day with work logged. Select a day to open it.'),
    ]),
    grid,
  ]);
}

// --- Totals -----------------------------------------------------------------

function renderTotals(days) {
  const count = (type) => days.filter((d) => d.dayType === type).length;
  const billable = days.reduce((sum, day) => sum + billableValue(day), 0);
  const km = days.reduce(
    (sum, day) => sum + day.expenses.reduce((n, x) => n + (Number(x.km) || 0), 0),
    0,
  );
  const expenses = days.reduce(
    (sum, day) => sum + day.expenses.reduce((n, x) => n + (Number(x.amount) || 0), 0),
    0,
  );

  const tiles = [
    [String(count('Full')), 'full days'],
    [String(count('Half')), 'half days'],
    [formatDays(billable), 'billable day equivalent'],
    [String(count('Non-billable')), 'non-billable days'],
    [formatMoney(expenses), 'expenses'],
    [km ? formatKm(km) : '0 km', 'distance'],
  ];

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [el('h2', { class: 'card__title' }, 'Totals')]),
    el(
      'div',
      { class: 'stats' },
      tiles.map(([value, label]) =>
        el('div', { class: 'stat' }, [
          el('span', { class: 'stat__value' }, value),
          el('span', { class: 'stat__label' }, label),
        ]),
      ),
    ),
  ]);
}

// --- Workstream split -------------------------------------------------------

/**
 * Days by workstream.
 *
 * A day counts once against every workstream it has done-work against, so the
 * shares deliberately add up to more than 100% when a day spans several. The
 * share is of days where anything was logged.
 */
function renderWorkstreams(days, settings) {
  const counts = new Map();
  let daysWithWork = 0;

  for (const day of days) {
    const streams = workstreamsForDay(day);
    if (!streams.length) continue;
    daysWithWork += 1;
    for (const stream of streams) {
      counts.set(stream, (counts.get(stream) || 0) + 1);
    }
  }

  const order = [
    ...settings.workstreams.filter((ws) => counts.has(ws)),
    ...[...counts.keys()].filter((ws) => !settings.workstreams.includes(ws)),
  ];

  const rows = order.map((ws) => {
    const count = counts.get(ws);
    const share = daysWithWork ? Math.round((count / daysWithWork) * 100) : 0;
    return el('div', { class: 'bar' }, [
      el('div', { class: 'bar__row' }, [
        el('span', { class: 'bar__label', title: ws }, ws),
        el('span', { class: 'bar__value' }, `${count} ${count === 1 ? 'day' : 'days'} · ${share}%`),
      ]),
      el('div', { class: 'bar__track' }, [
        el('div', { class: 'bar__fill', style: { width: `${share}%` } }),
      ]),
    ]);
  });

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Days by workstream'),
      el(
        'p',
        { class: 'card__hint' },
        daysWithWork
          ? `Share of the ${daysWithWork} ${daysWithWork === 1 ? 'day' : 'days'} with work logged. A day spanning two workstreams counts against both.`
          : '',
      ),
    ]),
    rows.length ? el('div', { class: 'bars' }, rows) : el('p', { class: 'empty' }, 'No work logged this month.'),
  ]);
}

// --- Expenses ---------------------------------------------------------------

function renderExpenses(days, settings) {
  const byCategory = new Map();
  let total = 0;
  let km = 0;
  let count = 0;

  for (const day of days) {
    for (const expense of day.expenses) {
      const amount = Number(expense.amount) || 0;
      const category = expense.category || 'Uncategorised';
      byCategory.set(category, (byCategory.get(category) || 0) + amount);
      total += amount;
      km += Number(expense.km) || 0;
      count += 1;
    }
  }

  const order = [
    ...settings.expenseCategories.filter((c) => byCategory.has(c)),
    ...[...byCategory.keys()].filter((c) => !settings.expenseCategories.includes(c)),
  ];

  const table = el('table', { class: 'table' }, [
    el('thead', [
      el('tr', [el('th', 'Category'), el('th', { class: 'num' }, 'Amount')]),
    ]),
    el(
      'tbody',
      order.map((category) =>
        el('tr', [el('td', category), el('td', { class: 'num' }, formatMoney(byCategory.get(category)))]),
      ),
    ),
    el('tfoot', [
      el('tr', [
        el('td', `Total (${count} ${count === 1 ? 'item' : 'items'})`),
        el('td', { class: 'num' }, formatMoney(total)),
      ]),
      km
        ? el('tr', [el('td', 'Total distance'), el('td', { class: 'num' }, formatKm(km))])
        : null,
    ]),
  ]);

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [el('h2', { class: 'card__title' }, 'Expenses by category')]),
    count ? el('div', { class: 'table-wrap' }, table) : el('p', { class: 'empty' }, 'No expenses this month.'),
  ]);
}
